import type { OutputDef, ReportDef, ValueDef } from "../../api/types";

export const emptyValue = (key: string, source: ValueDef["source"] = "auto"): ValueDef => ({
  key,
  code: null,
  name: null,
  type: source === "payload" ? "Number" : source === "input" || source === "flight" ? "Text" : "Analogue",
  units: null,
  aggregate: null,
  format: null,
  precision: null,
  source,
  conversion_type: null,
  conversion_table: null,
  options: {},
});

/** Inline template a fresh output starts with, so previews render before the user writes anything. */
export function starterTemplate(renderer: OutputDef["renderer"], format: OutputDef["format"] = "TXT"): string | null {
  if (renderer === "jinja2") {
    return "{{ snapshot.label }} report for {{ flight.tail }} at {{ window.start_time | date('%Y-%m-%d %H:%M:%S') }}\n{% for r in rows %}{{ r.token | pad(24) }} {{ r.formatted }} {{ r.units or '' }}\n{% endfor %}";
  }
  if (renderer === "legacy_bracket") {
    if (format === "SAGE_FORMAT") return "[TailId] [Phase]\n";
    if (format === "DIG") return "[ACID] [DATE]\n";
    return "ACID:[ACID]\nDATE:[DATE]\n";
  }
  return null;
}

export const emptyOutput = (name = "text", renderer: OutputDef["renderer"] = "jinja2"): OutputDef => ({
  name,
  renderer,
  template: null,
  template_text: starterTemplate(renderer),
  function: null,
  format: renderer === "legacy_bracket" ? "TXT" : null,
  filename: "[Report_Type]_[Tail]_[Flight_Date_Time].txt",
  media_type: "text/plain",
  encoding: "utf-8",
  newline: null,
  phase: null,
  report_type: null,
  enabled: true,
  options: {},
});

export const emptyReport = (name: string): ReportDef => ({
  name,
  description: "",
  triggers: [],
  labels: null,
  values: [],
  outputs: [emptyOutput()],
  max_per_flight: null,
  enabled: true,
});

/** Drop nulls/empties so the saved YAML stays short, but keep required fields. */
export function normaliseReport(r: ReportDef): ReportDef {
  return {
    ...r,
    labels: r.labels && r.labels.length ? r.labels : null,
    values: r.values.map((v) => ({ ...v, code: v.code?.trim() || null, units: v.units?.trim() || null, format: v.format?.trim() || null })),
    outputs: r.outputs.map((o) => ({
      ...o,
      template: o.template?.trim() || null,
      template_text: o.template ? null : o.template_text,
      function: o.function?.trim() || null,
      phase: o.phase?.trim() || null,
      report_type: o.report_type?.trim() || null,
      newline: o.newline === "" ? null : o.newline,
    })),
  };
}

export const MEDIA_TYPES = ["text/plain", "text/csv", "application/json", "text/html", "text/xml", "application/octet-stream"];
export const VALUE_TYPES = ["Analogue", "Discrete", "Time", "ISO #5", "BCD", "Text", "Number", "Array"];
